import React, { useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import moment from 'moment'
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import RefreshIcon from '@material-ui/icons/Refresh'
import { breakpoint } from 'src/theme/grid'
import AccountService from 'src/services/AccountService'
import { setSnackbarValuesRoutine } from 'src/ducks/actions'
import { DATE_LONG_FORMAT } from 'src/ducks/consts'
import Alert from 'src/components/Alert'
import TextInput from 'src/components/atoms/TextInput'
import { ButtonDark, ButtonOutlined } from 'src/components/atoms/Buttons'
import {
  Column,
  Row,
  TitleContainer
} from 'src/features/account/components/atoms'
import { Title } from 'src/features/account/components/typography'
import {
  Dialog,
  DialogText,
  ActionBar,
  ConfirmButton,
  CancelButton
} from 'src/components/Dialog'
import WebhookSettings from 'src/features/account/myProfile/WebhookSettings'

const API_TOKENS_QUERY_KEY = ['public-api-tokens']
const API_SCOPES_QUERY_KEY = ['public-api-token-scopes']
const TOKENS_PER_PAGE = 20
const MAX_TOKEN_NAME_LENGTH = 242

const getResponsePayload = response => response?.data || response

const normalizeScopesResponse = response => {
  const payload = getResponsePayload(response)

  if (Array.isArray(payload)) {
    return payload
  }

  return Array.isArray(payload?.data) ? payload.data : []
}

const normalizeTokensResponse = response => {
  const payload = getResponsePayload(response)
  const responseEnvelope = response?.response || {}
  let tokens = []

  if (Array.isArray(payload)) {
    tokens = payload
  } else if (Array.isArray(payload?.data)) {
    tokens = payload.data
  }

  const meta = responseEnvelope.meta || payload?.meta || {}

  return {
    tokens,
    meta: {
      currentPage: meta.currentPage || meta.current_page || 1,
      lastPage: meta.lastPage || meta.last_page || 1,
      perPage: meta.perPage || meta.per_page || TOKENS_PER_PAGE,
      total: meta.total || 0
    }
  }
}

const extractGeneratedToken = response => {
  const payload = getResponsePayload(response)

  return typeof payload?.token === 'string' ? payload.token : ''
}

const getErrorStatus = error => error?.status || error?.response?.status || null
const getErrorData = error => error?.data || error?.response?.data || {}

const getErrorMessages = error => {
  const errors = getErrorData(error)?.errors

  if (!errors || typeof errors !== 'object') {
    return []
  }

  return Object.values(errors)
    .reduce(
      (messages, value) =>
        messages.concat(Array.isArray(value) ? value : [value]),
      []
    )
    .filter(message => typeof message === 'string' && message)
}

const getRequestErrorMessage = (error, fallbackId, intl) => {
  const validationMessage = getErrorMessages(error)[0]
  const responseMessage = getErrorData(error)?.message

  if (validationMessage) {
    return validationMessage
  }

  if (getErrorStatus(error) === 403) {
    return intl.formatMessage({ id: 'account.apiKeys.accessError' })
  }

  if (typeof responseMessage === 'string' && responseMessage) {
    return responseMessage
  }

  return intl.formatMessage({ id: fallbackId })
}

const getTokenHint = token => token?.tokenHint || token?.token_hint || ''
const getTokenCreatedAt = token => token?.createdAt || token?.created_at || null
const getTokenLastUsedAt = token =>
  token?.lastUsedAt || token?.last_used_at || null
const getTokenExpiresAt = token => token?.expiresAt || token?.expires_at || null
const getTokenScopes = token =>
  Array.isArray(token?.scopes) ? token.scopes : []

const formatDate = value =>
  value && moment(value).isValid()
    ? moment(value).format(DATE_LONG_FORMAT)
    : null

const copyToClipboard = async value => {
  if (!value) {
    return false
  }

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value)
    return true
  }

  const temporaryInput = document.createElement('textarea')
  temporaryInput.value = value
  temporaryInput.setAttribute('readonly', '')
  temporaryInput.style.position = 'fixed'
  temporaryInput.style.opacity = '0'
  document.body.appendChild(temporaryInput)
  temporaryInput.select()
  const copied = document.execCommand('copy')
  document.body.removeChild(temporaryInput)

  return copied
}

const groupScopes = scopes =>
  scopes.reduce((groups, scope) => {
    const groupName = scope.group || scope.name?.split(':')[0] || 'other'

    return {
      ...groups,
      [groupName]: [...(groups[groupName] || []), scope]
    }
  }, {})

const getDefaultScopes = scopes =>
  scopes
    .filter(scope => scope.selectedByDefault || scope.selected_by_default)
    .map(scope => scope.name)

const getScopeMessageId = (type, value) =>
  `account.apiKeys.scope${type}.${value}`

const getScopeLabel = (scopeName, intl) => {
  const [group = scopeName, access = ''] = scopeName.split(':')
  const groupLabel = intl.formatMessage({
    id: getScopeMessageId('Group', group),
    defaultMessage: group.replace(/_/g, ' ')
  })
  const accessLabel = intl.formatMessage({
    id: getScopeMessageId('Access', access),
    defaultMessage: access
  })

  return `${groupLabel} · ${accessLabel}`
}

const ScopePicker = ({
  scopes,
  selectedScopes,
  onChange,
  intl,
  showSelectAll = false
}) => {
  const groupedScopes = useMemo(() => groupScopes(scopes), [scopes])
  const allScopeNames = scopes.map(scope => scope.name)
  const allScopesSelected =
    allScopeNames.length > 0 &&
    allScopeNames.every(scopeName => selectedScopes.includes(scopeName))

  const toggleScope = scopeName => {
    if (selectedScopes.includes(scopeName)) {
      onChange(selectedScopes.filter(name => name !== scopeName))
      return
    }

    onChange([...selectedScopes, scopeName])
  }

  return (
    <ScopePickerContainer>
      <ScopePickerHeader>
        <div>
          <ScopePickerTitle>
            <FormattedMessage id='account.apiKeys.scopes.title' />
          </ScopePickerTitle>
          <ScopePickerDescription>
            <FormattedMessage id='account.apiKeys.scopes.description' />
          </ScopePickerDescription>
        </div>
        <ScopePickerActions>
          <SelectionCounter>
            <FormattedMessage
              id='account.apiKeys.scopes.selected'
              values={{ count: selectedScopes.length }}
            />
          </SelectionCounter>
          {showSelectAll && (
            <ScopeBulkActions>
              <ScopeBulkButton
                type='button'
                disabled={allScopesSelected || !allScopeNames.length}
                onClick={() => onChange(allScopeNames)}
              >
                <FormattedMessage id='account.apiKeys.scopes.selectAll' />
              </ScopeBulkButton>
              <ScopeBulkButton
                type='button'
                disabled={!selectedScopes.length}
                onClick={() => onChange([])}
              >
                <FormattedMessage id='account.apiKeys.scopes.clearAll' />
              </ScopeBulkButton>
            </ScopeBulkActions>
          )}
        </ScopePickerActions>
      </ScopePickerHeader>

      <ScopeGroups>
        {Object.entries(groupedScopes).map(([groupName, groupItems]) => (
          <ScopeGroup key={groupName}>
            <ScopeGroupTitle>
              {intl.formatMessage({
                id: getScopeMessageId('Group', groupName),
                defaultMessage: groupName.replace(/_/g, ' ')
              })}
            </ScopeGroupTitle>
            <ScopeOptions>
              {groupItems.map(scope => {
                const selected = selectedScopes.includes(scope.name)

                return (
                  <ScopeOption key={scope.name} selected={selected}>
                    <ScopeCheckbox
                      type='checkbox'
                      id={`scope-${scope.name}`}
                      checked={selected}
                      onChange={() => toggleScope(scope.name)}
                    />
                    <ScopeOptionContent htmlFor={`scope-${scope.name}`}>
                      <ScopeOptionTopLine>
                        <ScopeAccess access={scope.access}>
                          {intl.formatMessage({
                            id: getScopeMessageId('Access', scope.access),
                            defaultMessage: scope.access
                          })}
                        </ScopeAccess>
                        <ScopeCode>{scope.name}</ScopeCode>
                      </ScopeOptionTopLine>
                      {scope.description && (
                        <ScopeDescription>{scope.description}</ScopeDescription>
                      )}
                    </ScopeOptionContent>
                  </ScopeOption>
                )
              })}
            </ScopeOptions>
          </ScopeGroup>
        ))}
      </ScopeGroups>
    </ScopePickerContainer>
  )
}

const ApiKeys = ({ className, isCompanyAdmin }) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [keyName, setKeyName] = useState('')
  const [selectedScopes, setSelectedScopes] = useState([])
  const [expiresAt, setExpiresAt] = useState('')
  const [createError, setCreateError] = useState('')
  const [editingToken, setEditingToken] = useState(null)
  const [editKeyName, setEditKeyName] = useState('')
  const [editScopes, setEditScopes] = useState([])
  const [editError, setEditError] = useState('')
  const [deleteConfirmToken, setDeleteConfirmToken] = useState(null)
  const [deleteError, setDeleteError] = useState('')
  const [isTokenDialogOpen, setIsTokenDialogOpen] = useState(false)
  const [newToken, setNewToken] = useState('')
  const [tokenCopied, setTokenCopied] = useState(false)

  const scopesQuery = useQuery({
    queryKey: API_SCOPES_QUERY_KEY,
    queryFn: AccountService.getPublicApiTokenScopes,
    select: normalizeScopesResponse,
    refetchOnWindowFocus: false
  })

  const tokensQuery = useQuery({
    queryKey: [...API_TOKENS_QUERY_KEY, page, TOKENS_PER_PAGE],
    queryFn: () =>
      AccountService.getPublicApiTokens({ page, limit: TOKENS_PER_PAGE }),
    select: normalizeTokensResponse,
    keepPreviousData: true,
    refetchOnWindowFocus: false
  })

  const createTokenMutation = useMutation({
    mutationFn: values => AccountService.createPublicApiToken(values),
    onSuccess: response => {
      const token = extractGeneratedToken(response)

      setIsCreateDialogOpen(false)
      setKeyName('')
      setSelectedScopes([])
      setExpiresAt('')
      setCreateError('')

      if (token) {
        setNewToken(token)
        setTokenCopied(false)
        setIsTokenDialogOpen(true)
      } else {
        queryClient.invalidateQueries({ queryKey: API_TOKENS_QUERY_KEY })
      }

      dispatch(
        setSnackbarValuesRoutine({
          type: 'success',
          title: '',
          message: intl.formatMessage({ id: 'account.apiKeys.createSuccess' })
        })
      )
    },
    onError: error => {
      setCreateError(
        getRequestErrorMessage(error, 'account.apiKeys.createError', intl)
      )
    }
  })

  const updateTokenMutation = useMutation({
    mutationFn: ({ id, values }) =>
      AccountService.updatePublicApiToken(id, values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: API_TOKENS_QUERY_KEY })
      dispatch(
        setSnackbarValuesRoutine({
          type: 'success',
          title: '',
          message: intl.formatMessage({ id: 'account.apiKeys.updateSuccess' })
        })
      )
      setEditingToken(null)
      setEditKeyName('')
      setEditScopes([])
      setEditError('')
    },
    onError: error => {
      setEditError(
        getRequestErrorMessage(error, 'account.apiKeys.updateError', intl)
      )
    }
  })

  const deleteTokenMutation = useMutation({
    mutationFn: tokenId => AccountService.deletePublicApiToken(tokenId),
    onSuccess: async () => {
      const shouldGoToPreviousPage =
        page > 1 && (tokensQuery.data?.tokens || []).length === 1

      await queryClient.invalidateQueries({ queryKey: API_TOKENS_QUERY_KEY })

      if (shouldGoToPreviousPage) {
        setPage(currentPage => currentPage - 1)
      }

      dispatch(
        setSnackbarValuesRoutine({
          type: 'success',
          title: '',
          message: intl.formatMessage({ id: 'account.apiKeys.deleteSuccess' })
        })
      )
      setDeleteConfirmToken(null)
      setDeleteError('')
    },
    onError: error => {
      setDeleteError(
        getRequestErrorMessage(error, 'account.apiKeys.deleteError', intl)
      )
    }
  })

  const scopes = scopesQuery.data || []
  const tokens = tokensQuery.data?.tokens || []
  const pagination = tokensQuery.data?.meta || {
    currentPage: page,
    lastPage: 1,
    total: 0
  }

  const normalizedKeyName = keyName.trim()
  const normalizedEditKeyName = editKeyName.trim()
  const parsedExpiration = expiresAt ? moment(expiresAt) : null
  const expirationIsValid =
    !parsedExpiration ||
    (parsedExpiration.isValid() && parsedExpiration.isAfter(moment()))
  const createFormIsValid =
    normalizedKeyName.length > 0 &&
    normalizedKeyName.length <= MAX_TOKEN_NAME_LENGTH &&
    selectedScopes.length > 0 &&
    expirationIsValid
  const editFormIsValid =
    normalizedEditKeyName.length > 0 &&
    normalizedEditKeyName.length <= MAX_TOKEN_NAME_LENGTH &&
    editScopes.length > 0
  const isRefreshing = tokensQuery.isFetching || scopesQuery.isFetching

  const openCreateDialog = () => {
    setKeyName('')
    setSelectedScopes(getDefaultScopes(scopes))
    setExpiresAt('')
    setCreateError('')
    setIsCreateDialogOpen(true)
  }

  const closeCreateDialog = () => {
    if (createTokenMutation.isLoading) {
      return
    }

    setIsCreateDialogOpen(false)
    setKeyName('')
    setSelectedScopes([])
    setExpiresAt('')
    setCreateError('')
  }

  const openEditDialog = token => {
    setEditingToken(token)
    setEditKeyName(token.name || '')
    setEditScopes(getTokenScopes(token))
    setEditError('')
  }

  const closeEditDialog = () => {
    if (updateTokenMutation.isLoading) {
      return
    }

    setEditingToken(null)
    setEditKeyName('')
    setEditScopes([])
    setEditError('')
  }

  const closeTokenDialog = () => {
    setIsTokenDialogOpen(false)
    setNewToken('')
    setTokenCopied(false)
    createTokenMutation.reset()
    setPage(1)
    queryClient.invalidateQueries({ queryKey: API_TOKENS_QUERY_KEY })
  }

  const handleCopyToken = async () => {
    try {
      const copied = await copyToClipboard(newToken)
      setTokenCopied(copied)

      if (copied) {
        dispatch(
          setSnackbarValuesRoutine({
            type: 'success',
            title: '',
            message: intl.formatMessage({ id: 'account.apiKeys.copySuccess' })
          })
        )
      }
    } catch (error) {
      setTokenCopied(false)
    }
  }

  const submitCreate = () => {
    if (!createFormIsValid) {
      return
    }

    const values = {
      name: normalizedKeyName,
      scopes: selectedScopes
    }

    if (parsedExpiration) {
      values.expiresAt = parsedExpiration.utc().toISOString()
    }

    setCreateError('')
    createTokenMutation.mutate(values)
  }

  const submitEdit = () => {
    if (!editingToken?.id || !editFormIsValid) {
      return
    }

    setEditError('')
    updateTokenMutation.mutate({
      id: editingToken.id,
      values: {
        name: normalizedEditKeyName,
        scopes: editScopes
      }
    })
  }

  let tokensContent = null

  if (tokensQuery.isLoading) {
    tokensContent = (
      <StateText>
        <FormattedMessage id='common.loading' />
      </StateText>
    )
  } else if (tokensQuery.isError) {
    tokensContent = (
      <ErrorState>
        <Alert
          fullWidth
          type='alert'
          message={getRequestErrorMessage(
            tokensQuery.error,
            'account.apiKeys.loadError',
            intl
          )}
        />
        <ButtonOutlined
          active={!tokensQuery.isFetching}
          loading={tokensQuery.isFetching}
          disabled={tokensQuery.isFetching}
          onClick={() => tokensQuery.refetch()}
        >
          <FormattedMessage id='account.apiKeys.retryButton' />
        </ButtonOutlined>
      </ErrorState>
    )
  } else if (!tokens.length) {
    tokensContent = (
      <EmptyState>
        <EmptyStateTitle>
          <FormattedMessage id='account.apiKeys.empty' />
        </EmptyStateTitle>
        <EmptyStateText>
          <FormattedMessage id='account.apiKeys.emptyDescription' />
        </EmptyStateText>
      </EmptyState>
    )
  } else {
    tokensContent = (
      <TokensList>
        {tokens.map(token => {
          const tokenName = token.name || token.id
          const tokenHint = getTokenHint(token)
          const createdAt = formatDate(getTokenCreatedAt(token))
          const lastUsedAt = formatDate(getTokenLastUsedAt(token))
          const expiresAtValue = formatDate(getTokenExpiresAt(token))
          const tokenScopes = getTokenScopes(token)
          const expired = token.status === 'expired'

          return (
            <TokenRow key={token.id || tokenName} expired={expired}>
              <TokenMain>
                <TokenHeading>
                  <TokenIdentity>
                    <TokenName>{tokenName}</TokenName>
                    {tokenHint && <TokenHint>{tokenHint}</TokenHint>}
                  </TokenIdentity>
                  <StatusBadge expired={expired}>
                    <StatusDot expired={expired} />
                    <FormattedMessage
                      id={
                        expired
                          ? 'account.apiKeys.status.expired'
                          : 'account.apiKeys.status.active'
                      }
                    />
                  </StatusBadge>
                </TokenHeading>

                <TokenMetadata>
                  <MetadataItem>
                    <MetadataLabel>
                      <FormattedMessage id='account.apiKeys.createdAt' />
                    </MetadataLabel>
                    <MetadataValue>{createdAt || '—'}</MetadataValue>
                  </MetadataItem>
                  <MetadataItem>
                    <MetadataLabel>
                      <FormattedMessage id='account.apiKeys.expiresAt' />
                    </MetadataLabel>
                    <MetadataValue>
                      {expiresAtValue || (
                        <FormattedMessage id='account.apiKeys.neverExpires' />
                      )}
                    </MetadataValue>
                  </MetadataItem>
                  <MetadataItem>
                    <MetadataLabel>
                      <FormattedMessage id='account.apiKeys.lastUsedAt' />
                    </MetadataLabel>
                    <MetadataValue>
                      {lastUsedAt || (
                        <FormattedMessage id='account.apiKeys.neverUsed' />
                      )}
                    </MetadataValue>
                  </MetadataItem>
                </TokenMetadata>

                <ScopeChips
                  aria-label={intl.formatMessage({
                    id: 'account.apiKeys.scopes.title'
                  })}
                >
                  {tokenScopes.map(scopeName => (
                    <ScopeChip key={scopeName} title={scopeName}>
                      {getScopeLabel(scopeName, intl)}
                    </ScopeChip>
                  ))}
                </ScopeChips>
              </TokenMain>

              <TokenActions>
                <IconButton
                  type='button'
                  onClick={() => openEditDialog(token)}
                  disabled={
                    !token.id || scopesQuery.isLoading || scopesQuery.isError
                  }
                  title={intl.formatMessage({ id: 'common.edit' })}
                  aria-label={`${intl.formatMessage({
                    id: 'common.edit'
                  })}: ${tokenName}`}
                >
                  <EditOutlinedIcon fontSize='small' />
                </IconButton>
                <IconButton
                  type='button'
                  danger
                  onClick={() => {
                    setDeleteError('')
                    setDeleteConfirmToken(token)
                  }}
                  disabled={!token.id}
                  title={intl.formatMessage({ id: 'common.remove' })}
                  aria-label={`${intl.formatMessage({
                    id: 'common.remove'
                  })}: ${tokenName}`}
                >
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              </TokenActions>
            </TokenRow>
          )
        })}
      </TokensList>
    )
  }

  return (
    <div className={className}>
      <SectionBlock>
        <WebhookSettings readOnly={!isCompanyAdmin} />
      </SectionBlock>

      <SectionBlock>
        <Row>
          <Column left>
            <TitleContainer>
              <Title>
                <FormattedMessage id='account.apiKeys' />
              </Title>
            </TitleContainer>
            <Alert
              fullWidth
              type='info'
              message={intl.formatMessage({ id: 'account.apiKeys.info' })}
            />
          </Column>
        </Row>

        {scopesQuery.isError && (
          <Row>
            <Column>
              <Alert
                fullWidth
                type='warning'
                message={intl.formatMessage({
                  id: 'account.apiKeys.scopes.loadError'
                })}
              />
            </Column>
          </Row>
        )}

        <Row>
          <Column>
            <Toolbar>
              <ActionsRow>
                <ButtonDark
                  active={
                    !createTokenMutation.isLoading &&
                    !scopesQuery.isLoading &&
                    !scopesQuery.isError
                  }
                  disabled={
                    createTokenMutation.isLoading ||
                    scopesQuery.isLoading ||
                    scopesQuery.isError
                  }
                  onClick={openCreateDialog}
                >
                  <AddMark aria-hidden='true'>+</AddMark>
                  <FormattedMessage id='account.apiKeys.createButton' />
                </ButtonDark>
                <ButtonOutlined
                  active={!isRefreshing}
                  loading={isRefreshing}
                  disabled={isRefreshing}
                  onClick={() => {
                    tokensQuery.refetch()
                    scopesQuery.refetch()
                  }}
                >
                  <RefreshIcon fontSize='small' />
                  <FormattedMessage id='account.apiKeys.refreshButton' />
                </ButtonOutlined>
              </ActionsRow>
              {!tokensQuery.isLoading && !tokensQuery.isError && (
                <TokensTotal>
                  <FormattedMessage
                    id='account.apiKeys.total'
                    values={{ count: pagination.total }}
                  />
                </TokensTotal>
              )}
            </Toolbar>
          </Column>
        </Row>

        <Row>
          <Column>
            {tokensContent}
            {pagination.lastPage > 1 && !tokensQuery.isError && (
              <PaginationBar>
                <PageButton
                  type='button'
                  disabled={page <= 1 || tokensQuery.isFetching}
                  onClick={() => setPage(currentPage => currentPage - 1)}
                >
                  <FormattedMessage id='account.apiKeys.pagination.previous' />
                </PageButton>
                <PageIndicator>
                  <FormattedMessage
                    id='account.apiKeys.pagination.page'
                    values={{
                      current: pagination.currentPage,
                      total: pagination.lastPage
                    }}
                  />
                </PageIndicator>
                <PageButton
                  type='button'
                  disabled={
                    page >= pagination.lastPage || tokensQuery.isFetching
                  }
                  onClick={() => setPage(currentPage => currentPage + 1)}
                >
                  <FormattedMessage id='account.apiKeys.pagination.next' />
                </PageButton>
              </PaginationBar>
            )}
          </Column>
        </Row>

        <Dialog
          title={intl.formatMessage({
            id: 'account.apiKeys.createDialog.title'
          })}
          open={isCreateDialogOpen}
          onClose={closeCreateDialog}
          maxWidth='md'
          disableBackdropClick={createTokenMutation.isLoading}
        >
          <DialogText>
            <FormattedMessage id='account.apiKeys.createDialog.description' />
          </DialogText>

          {createError && (
            <ModalAlert fullWidth type='alert' message={createError} />
          )}

          <DialogInputWrapper>
            <TextInput
              label={intl.formatMessage({
                id: 'account.apiKeys.createDialog.nameLabel'
              })}
              name='keyName'
              id='keyName'
              placeholder={intl.formatMessage({
                id: 'account.apiKeys.createDialog.namePlaceholder'
              })}
              helperText={intl.formatMessage(
                { id: 'account.apiKeys.nameHelper' },
                { max: MAX_TOKEN_NAME_LENGTH }
              )}
              value={keyName}
              onChange={(_, value) => {
                setCreateError('')
                setKeyName(value)
              }}
              validate={() => true}
              required
            />
            {keyName.length > MAX_TOKEN_NAME_LENGTH && (
              <FieldError>
                <FormattedMessage
                  id='account.apiKeys.nameTooLong'
                  values={{ max: MAX_TOKEN_NAME_LENGTH }}
                />
              </FieldError>
            )}
          </DialogInputWrapper>

          <DialogInputWrapper>
            <NativeLabel htmlFor='api-key-expires-at'>
              <FormattedMessage id='account.apiKeys.createDialog.expiresAtLabel' />
              <OptionalLabel>
                <FormattedMessage id='account.apiKeys.optional' />
              </OptionalLabel>
            </NativeLabel>
            <DateTimeInput
              id='api-key-expires-at'
              name='expiresAt'
              type='datetime-local'
              min={moment()
                .add(1, 'minute')
                .format('YYYY-MM-DDTHH:mm')}
              value={expiresAt}
              onChange={event => {
                setCreateError('')
                setExpiresAt(event.target.value)
              }}
              invalid={!expirationIsValid}
            />
            <FieldHelper invalid={!expirationIsValid}>
              <FormattedMessage
                id={
                  expirationIsValid
                    ? 'account.apiKeys.createDialog.expiresAtHelper'
                    : 'account.apiKeys.createDialog.expiresAtInvalid'
                }
              />
            </FieldHelper>
          </DialogInputWrapper>

          <ScopePicker
            scopes={scopes}
            selectedScopes={selectedScopes}
            onChange={nextScopes => {
              setCreateError('')
              setSelectedScopes(nextScopes)
            }}
            intl={intl}
          />
          {!selectedScopes.length && (
            <FieldError>
              <FormattedMessage id='account.apiKeys.scopes.required' />
            </FieldError>
          )}

          <ActionBar>
            <CancelButton
              disabled={createTokenMutation.isLoading}
              onClick={closeCreateDialog}
            >
              <FormattedMessage id='common.cancel' />
            </CancelButton>
            <ConfirmButton
              disabled={!createFormIsValid || createTokenMutation.isLoading}
              onClick={submitCreate}
            >
              <FormattedMessage id='account.apiKeys.createButton' />
            </ConfirmButton>
          </ActionBar>
        </Dialog>

        <Dialog
          title={intl.formatMessage({
            id: 'account.apiKeys.editDialog.title'
          })}
          open={Boolean(editingToken)}
          onClose={closeEditDialog}
          maxWidth='md'
          disableBackdropClick={updateTokenMutation.isLoading}
        >
          <DialogText>
            <FormattedMessage id='account.apiKeys.editDialog.description' />
          </DialogText>

          {editError && (
            <ModalAlert fullWidth type='alert' message={editError} />
          )}

          {editingToken && (
            <TokenInfoBox>
              <TokenInfoIdentity>
                <strong>{editingToken.name || editingToken.id}</strong>
                {getTokenHint(editingToken) && (
                  <TokenHintDisplay>
                    {getTokenHint(editingToken)}
                  </TokenHintDisplay>
                )}
              </TokenInfoIdentity>
              <TokenInfoNotice>
                <FormattedMessage id='account.apiKeys.editDialog.expirationNotice' />
              </TokenInfoNotice>
            </TokenInfoBox>
          )}

          <DialogInputWrapper>
            <TextInput
              label={intl.formatMessage({
                id: 'account.apiKeys.createDialog.nameLabel'
              })}
              name='editKeyName'
              id='editKeyName'
              placeholder={intl.formatMessage({
                id: 'account.apiKeys.createDialog.namePlaceholder'
              })}
              helperText={intl.formatMessage(
                { id: 'account.apiKeys.nameHelper' },
                { max: MAX_TOKEN_NAME_LENGTH }
              )}
              value={editKeyName}
              onChange={(_, value) => {
                setEditError('')
                setEditKeyName(value)
              }}
              validate={() => true}
              required
            />
            {editKeyName.length > MAX_TOKEN_NAME_LENGTH && (
              <FieldError>
                <FormattedMessage
                  id='account.apiKeys.nameTooLong'
                  values={{ max: MAX_TOKEN_NAME_LENGTH }}
                />
              </FieldError>
            )}
          </DialogInputWrapper>

          <ScopePicker
            scopes={scopes}
            selectedScopes={editScopes}
            onChange={nextScopes => {
              setEditError('')
              setEditScopes(nextScopes)
            }}
            intl={intl}
            showSelectAll
          />
          {!editScopes.length && (
            <FieldError>
              <FormattedMessage id='account.apiKeys.scopes.required' />
            </FieldError>
          )}

          <ActionBar>
            <CancelButton
              disabled={updateTokenMutation.isLoading}
              onClick={closeEditDialog}
            >
              <FormattedMessage id='common.cancel' />
            </CancelButton>
            <ConfirmButton
              disabled={!editFormIsValid || updateTokenMutation.isLoading}
              onClick={submitEdit}
            >
              <FormattedMessage id='common.save' />
            </ConfirmButton>
          </ActionBar>
        </Dialog>

        <Dialog
          title={intl.formatMessage({
            id: 'account.apiKeys.deleteConfirm.title'
          })}
          open={Boolean(deleteConfirmToken)}
          onClose={() => {
            if (!deleteTokenMutation.isLoading) {
              setDeleteConfirmToken(null)
              setDeleteError('')
            }
          }}
          disableBackdropClick={deleteTokenMutation.isLoading}
        >
          <DialogText>
            <FormattedMessage id='account.apiKeys.deleteConfirm.message' />
          </DialogText>

          {deleteError && (
            <ModalAlert fullWidth type='alert' message={deleteError} />
          )}

          {deleteConfirmToken && (
            <TokenInfoBox danger>
              <TokenInfoIdentity>
                <strong>
                  {deleteConfirmToken.name || deleteConfirmToken.id}
                </strong>
                {getTokenHint(deleteConfirmToken) && (
                  <TokenHintDisplay>
                    {getTokenHint(deleteConfirmToken)}
                  </TokenHintDisplay>
                )}
              </TokenInfoIdentity>
            </TokenInfoBox>
          )}
          <ActionBar>
            <CancelButton
              disabled={deleteTokenMutation.isLoading}
              onClick={() => {
                setDeleteConfirmToken(null)
                setDeleteError('')
              }}
            >
              <FormattedMessage id='common.cancel' />
            </CancelButton>
            <DangerConfirmButton
              disabled={deleteTokenMutation.isLoading}
              onClick={() => {
                if (deleteConfirmToken?.id) {
                  setDeleteError('')
                  deleteTokenMutation.mutate(deleteConfirmToken.id)
                }
              }}
            >
              <FormattedMessage id='common.remove' />
            </DangerConfirmButton>
          </ActionBar>
        </Dialog>

        <Dialog
          title={intl.formatMessage({
            id: 'account.apiKeys.tokenGeneratedTitle'
          })}
          open={isTokenDialogOpen}
          onClose={closeTokenDialog}
          hideCloseIcon
          disableBackdropClick
          disableEscapeKeyDown
        >
          <DialogText>
            <FormattedMessage id='account.apiKeys.tokenGeneratedDescription' />
          </DialogText>
          <DialogInputWrapper>
            <NativeLabel htmlFor='new-api-token'>
              <FormattedMessage id='account.apiKeys.token' />
            </NativeLabel>
            <KeyRow>
              <SecretInput
                id='new-api-token'
                value={newToken}
                readOnly
                spellCheck='false'
                autoComplete='off'
              />
              <CopyButton
                type='button'
                onClick={handleCopyToken}
                copied={tokenCopied}
              >
                <FileCopyOutlinedIcon fontSize='small' />
                <FormattedMessage
                  id={
                    tokenCopied
                      ? 'account.apiKeys.copiedButton'
                      : 'account.apiKeys.copyButton'
                  }
                />
              </CopyButton>
            </KeyRow>
          </DialogInputWrapper>
          <Alert
            fullWidth
            type='warning'
            message={intl.formatMessage({
              id: 'account.apiKeys.tokenWarning'
            })}
          />
          <ActionBar>
            <ConfirmButton onClick={closeTokenDialog}>
              <FormattedMessage id='account.apiKeys.savedTokenButton' />
            </ConfirmButton>
          </ActionBar>
        </Dialog>
      </SectionBlock>
    </div>
  )
}

export default styled(ApiKeys)`
  ${Alert} {
    margin: 0 0 1rem 0;
  }
`

const SectionBlock = styled.div`
  &:not(:last-child) {
    margin-bottom: 2rem;
  }
`

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-bottom: 1rem;

  ${breakpoint.xs`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `}
`

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;

  ${ButtonDark},
  ${ButtonOutlined} {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    min-width: auto;
    height: 2.25rem;
    line-height: 1;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
`

const AddMark = styled.span`
  font-size: 1rem;
  font-weight: 800;
`

const TokensTotal = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.62).hex()};
`

const TokensList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const TokenRow = styled.article`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  background: ${({ theme }) => theme.colors.white.hex()};
  border: 1px solid
    ${({ theme, expired }) =>
      expired
        ? theme.colors.ghost.alpha(0.48).hex()
        : theme.colors.apple.alpha(0.28).hex()};
  border-left: 3px solid
    ${({ theme, expired }) =>
      expired ? theme.colors.ghost.hex() : theme.colors.apple.hex()};
  border-radius: 4px;
  opacity: ${({ expired }) => (expired ? 0.78 : 1)};
`

const TokenMain = styled.div`
  min-width: 0;
  flex: 1;
`

const TokenHeading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;

  ${breakpoint.xs`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `}
`

const TokenIdentity = styled.div`
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`

const TokenName = styled.h3`
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  word-break: break-word;
`

const TokenHint = styled.code`
  font-size: 0.72rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.68).hex()};
  background: ${({ theme }) => theme.colors.wildSand.hex()};
  padding: 0.16rem 0.45rem;
  border-radius: 3px;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: ${({ theme, expired }) =>
    expired
      ? theme.colors.ghost.alpha(0.2).hex()
      : theme.colors.apple.alpha(0.12).hex()};
  color: ${({ theme, expired }) =>
    expired
      ? theme.colors.ebonyClay.alpha(0.68).hex()
      : theme.colors.apple.darken(0.3).hex()};
  font-size: 0.7rem;
  font-weight: 700;
`

const StatusDot = styled.span`
  width: 0.42rem;
  height: 0.42rem;
  border-radius: 50%;
  background: ${({ theme, expired }) =>
    expired ? theme.colors.ghost.hex() : theme.colors.apple.hex()};
`

const TokenMetadata = styled.dl`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem 1.2rem;
  margin: 0.8rem 0 0;

  ${breakpoint.xs`
    grid-template-columns: repeat(3, minmax(0, 1fr));
  `}
`

const MetadataItem = styled.div`
  min-width: 0;
`

const MetadataLabel = styled.dt`
  margin: 0 0 0.12rem;
  font-size: 0.68rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.52).hex()};
`

const MetadataValue = styled.dd`
  margin: 0;
  font-size: 0.76rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.82).hex()};
`

const ScopeChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.8rem;
`

const ScopeChip = styled.span`
  display: inline-flex;
  padding: 0.2rem 0.48rem;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.42).hex()};
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.74).hex()};
  font-size: 0.67rem;
  line-height: 1.3;
`

const TokenActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 0.25rem;
`

const IconButton = styled.button`
  background: transparent;
  border: none;
  padding: 0.48rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease, color 0.2s ease;
  color: ${({ theme, danger }) =>
    danger ? theme.colors.fadedRed.hex() : theme.colors.ebonyClay.hex()};

  &:hover:not(:disabled),
  &:focus-visible {
    background: ${({ theme }) => theme.colors.ghost.alpha(0.2).hex()};
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.easternBlue.hex()};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`

const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  border: 1px dashed ${({ theme }) => theme.colors.ghost.alpha(0.6).hex()};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white.alpha(0.55).hex()};
`

const EmptyStateTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
`

const EmptyStateText = styled.div`
  margin-top: 0.3rem;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.62).hex()};
`

const StateText = styled.div`
  padding: 1rem 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.6).hex()};
`

const ErrorState = styled.div`
  ${ButtonOutlined} {
    margin-top: 0.75rem;
    min-width: auto;
    height: 2.25rem;
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`

const PaginationBar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 1rem;
`

const PageButton = styled.button`
  min-height: 2rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.5).hex()};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.apple.hex()};
    outline: none;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const PageIndicator = styled.span`
  font-size: 0.76rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.7).hex()};
`

const DialogInputWrapper = styled.div`
  margin: 1rem 0;
`

const ModalAlert = styled(Alert)`
  margin: 0 0 1rem !important;
`

const NativeLabel = styled.label`
  display: flex;
  align-items: baseline;
  width: 100%;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 600;
  font-size: 0.75rem;
  margin: 0 0 0.3rem 0.2rem;
`

const OptionalLabel = styled.span`
  margin-left: 0.25rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black.alpha(0.62).hex()};
`

const DateTimeInput = styled.input`
  width: 100%;
  height: 42px;
  box-sizing: border-box;
  padding: 0.55rem 0.7rem;
  border: 1px solid
    ${({ theme, invalid }) =>
      invalid ? theme.colors.fadedRed.hex() : '#c6cbd4'};
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.8).hex()};
  font: inherit;

  &:focus {
    border-color: ${({ theme }) => theme.colors.easternBlue.hex()};
    box-shadow: 0 2px 10px 0 rgba(19, 162, 191, 0.2);
    outline: none;
  }
`

const FieldHelper = styled.span`
  display: block;
  margin: 0.45rem 0.2rem 0;
  font-size: 0.72rem;
  line-height: 1.35;
  color: ${({ theme, invalid }) =>
    invalid
      ? theme.colors.fadedRed.hex()
      : theme.colors.ebonyClay.alpha(0.58).hex()};
`

const FieldError = styled.div`
  margin-top: 0.45rem;
  color: ${({ theme }) => theme.colors.fadedRed.hex()};
  font-size: 0.72rem;
`

const ScopePickerContainer = styled.section`
  margin-top: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.45).hex()};
  border-radius: 4px;
  overflow: hidden;
`

const ScopePickerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  background: ${({ theme }) => theme.colors.wildSand.hex()};
  border-bottom: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.4).hex()};

  ${breakpoint.xs`
    flex-direction: row;
    align-items: center;
  `}
`

const ScopePickerTitle = styled.h4`
  margin: 0;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
`

const ScopePickerDescription = styled.p`
  margin: 0.2rem 0 0;
  font-size: 0.72rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.62).hex()};
`

const SelectionCounter = styled.span`
  flex-shrink: 0;
  padding: 0.22rem 0.5rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.72).hex()};
  font-size: 0.7rem;
  font-weight: 700;
`

const ScopePickerActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
`

const ScopeBulkActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
`

const ScopeBulkButton = styled.button`
  min-height: 1.55rem;
  padding: 0.18rem 0.42rem;
  border: 1px solid ${({ theme }) => theme.colors.apple.alpha(0.55).hex()};
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.white.hex()};
  color: ${({ theme }) => theme.colors.apple.darken(0.25).hex()};
  font-size: 0.62rem;
  line-height: 1.2;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.apple.hex()};
    background: ${({ theme }) => theme.colors.apple.alpha(0.08).hex()};
    outline: none;
  }

  &:disabled {
    opacity: 0.48;
    cursor: not-allowed;
  }
`

const ScopeGroups = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  max-height: 48vh;
  overflow-y: auto;

  ${breakpoint.s`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}
`

const ScopeGroup = styled.div`
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.3).hex()};

  ${breakpoint.s`
    &:nth-child(odd) {
      border-right: 1px solid ${({ theme }) =>
        theme.colors.ghost.alpha(0.3).hex()};
    }
  `}
`

const ScopeGroupTitle = styled.h5`
  margin: 0 0 0.45rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.76).hex()};
`

const ScopeOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const ScopeOption = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.55rem;
  border: 1px solid transparent;
  border-radius: 3px;
  transition: background-color 0.18s ease, border-color 0.18s ease;

  ${({ selected, theme }) =>
    selected &&
    css`
      border-color: ${theme.colors.apple.alpha(0.32).hex()};
      background: ${theme.colors.apple.alpha(0.06).hex()};
    `}
`

const ScopeCheckbox = styled.input`
  margin: 0.18rem 0 0;
  accent-color: ${({ theme }) => theme.colors.apple.hex()};
  cursor: pointer;
`

const ScopeOptionContent = styled.label`
  display: block;
  min-width: 0;
  flex: 1;
  cursor: pointer;
`

const ScopeOptionTopLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const ScopeAccess = styled.span`
  padding: 0.12rem 0.36rem;
  border-radius: 2px;
  background: ${({ theme, access }) =>
    access === 'write'
      ? theme.colors.easternBlue.alpha(0.12).hex()
      : theme.colors.apple.alpha(0.12).hex()};
  color: ${({ theme, access }) =>
    access === 'write'
      ? theme.colors.easternBlue.darken(0.22).hex()
      : theme.colors.apple.darken(0.28).hex()};
  font-size: 0.64rem;
  font-weight: 800;
  text-transform: uppercase;
`

const ScopeCode = styled.code`
  font-size: 0.62rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.52).hex()};
  word-break: break-all;
`

const ScopeDescription = styled.p`
  margin: 0.3rem 0 0;
  font-size: 0.68rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.62).hex()};
`

const TokenInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  margin: 0.75rem 0 1rem;
  border-left: 3px solid
    ${({ theme, danger }) =>
      danger ? theme.colors.fadedRed.hex() : theme.colors.easternBlue.hex()};
  background: ${({ theme }) => theme.colors.wildSand.hex()};
  border-radius: 3px;
`

const TokenInfoIdentity = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
`

const TokenInfoNotice = styled.div`
  font-size: 0.72rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.62).hex()};
`

const TokenHintDisplay = styled.code`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.7).hex()};
`

const DangerConfirmButton = styled(ConfirmButton)`
  background-color: ${({ theme }) => theme.colors.fadedRed.hex()};
  box-shadow: 0 2px 10px 0 rgba(198, 67, 67, 0.22);

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.fadedRed.darken(0.1).hex()};
  }
`

const KeyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  align-items: stretch;

  ${breakpoint.xs`
    grid-template-columns: minmax(0, 1fr) auto;
  `}
`

const SecretInput = styled.input`
  width: 100%;
  min-width: 0;
  height: 42px;
  box-sizing: border-box;
  padding: 0.55rem 0.7rem;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.55).hex()};
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.wildSand.hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-family: 'Courier New', monospace;
  font-size: 0.76rem;

  &:focus {
    border-color: ${({ theme }) => theme.colors.easternBlue.hex()};
    outline: none;
  }
`

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 42px;
  padding: 0.55rem 0.85rem;
  border: 1px solid
    ${({ theme, copied }) =>
      copied ? theme.colors.apple.hex() : theme.colors.ghost.alpha(0.55).hex()};
  border-radius: 3px;
  background: ${({ theme, copied }) =>
    copied ? theme.colors.apple.alpha(0.1).hex() : theme.colors.white.hex()};
  color: ${({ theme, copied }) =>
    copied
      ? theme.colors.apple.darken(0.25).hex()
      : theme.colors.ebonyClay.hex()};
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.apple.hex()};
    outline: none;
  }
`
